import Head from "next/head";
import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { WEB_URL } from "@/config/endpoints";
import IMAGES from "@/public/assets/img";
import MachineryLoanData from "@/config/machineryLoan_config";
import { useRouter } from "next/router";
import ContactFormStep1 from "@/component/shared/contactForm/contactFormStep1";
import ContactFormStep2 from "@/component/shared/contactForm/contactFormStep2";
import { FormProvider } from "@/component/shared/contactForm/formContext";
import ExporterChallenge from "@/component/shared/machinery-loan/exporterChallenge";
import HowWork from "@/component/shared/machinery-loan/HowWork";
import BuyerCredit from "@/component/shared/machinery-loan/buyer-credit";
import AccordianFaq from "@/component/shared/accordianfaq";
import BannerCTA from "@/component/shared/machinery-loan/banner-CTA";
import { generateFaqSchema } from "@/component/shared/faqSchema";

const MachineryLoan = () => {
  const router = useRouter();
  const [mWidth, setMWidth] = useState(0);
  const [showNextStep, setShowNextStep] = useState(false);
  const topSectionRef = useRef(null);
  const faqSchema = generateFaqSchema(MachineryLoanData.Faq);
  const handleWindowResize = () => {
    setMWidth(window.innerWidth);
  };

  useEffect(() => {
    handleWindowResize();
    window.addEventListener("load", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  const showSteps = () => {
    setShowNextStep(true);
  };
  
  const showFirstStep = () => {
    setShowNextStep(false);
  };

  return (
    <main className="pt-[110px]">
      <Head>
         <title>{MachineryLoanData.seoData.title}</title>
          <meta name="description" content={MachineryLoanData.seoData.description} />
          {/* OpenGraph tags */} 
          <meta property="og:title" content={MachineryLoanData.seoData.title}/>
          <meta property="og:description" content={MachineryLoanData.seoData.description} />
          <meta property="og:image" content={IMAGES.HOMEIMG.bigLogoNew.src} />
          <meta property="og:url" content={WEB_URL+router.asPath} />
          <link rel="canonical" href={WEB_URL+router.asPath} />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: JSON.stringify(faqSchema)}}/>
      </Head>

      <section
        ref={topSectionRef}
        className={`relative bg-no-repeat sm:bg-center bg-cover bg-bottom min-h-[450px] sm:min-h-[270px]`}
      >
         {mWidth > 768 ? 
        <Image
          priority={true}
          src={IMAGES.MachineryLoan.bgBannerDesktop}
          width={1366}
          height={550}
          className="absolute inset-0 max-w-none w-full h-full"
          alt="Banner Desktop"
        /> :
        <Image
          priority={true}
          src={IMAGES.MachineryLoan.bgBannerMobile}
          width={767}
          height={451}
          className="absolute inset-0 max-w-none w-full h-full"
          alt="Banner Mobile"
        />
      }
        <div className="max-w-[1200px] mx-auto relative px-3 xm:px-2 flex justify-center items-center sm:flex-col pb-3 sm:pt-5">
          <div className="w-1/2 sm:w-full">
            <h2 className="text-[50px] leading-[63px] md:text-heading5 sm:text-3xl font-medium text-[#072F5F]">
              {MachineryLoanData.bannerHeading}
            </h2>
            <h2 className="text-heading5  md:text-3xl sm:text-2xl text-[#072F5F] font-medium mb-2">
              {MachineryLoanData.subheading}
            </h2>
            <div className="bg-[#E3A003] w-[90%] text-center py-xsp px-4 rounded-xl mb-4 text-3xl sm:text-lg text-white uppercase">
              {MachineryLoanData.btntext}
            </div>
            <h1
              className="text-[#072F5F] text-xxl sm:text-lg mb-4 w-[90%] sm:w-full"
              dangerouslySetInnerHTML={{ __html: MachineryLoanData.paraLoan }}
            />
            <div className="flex gap-6 sm:gap-1 justify-between">
              {MachineryLoanData.loanInfo.map((item, index) => (
                <div
                  key={index}
                  className={`
                  ${index === 0 ? "sm:w-1/4" : "sm:w-[30%]"}
                `}
                >
                  <Image
                    src={item.icon}
                    alt={item.description}
                    width={40}
                    height={40}
                    className="mb-1 h-12"
                  />
                  <p className="text-[#1A1A1A] text-base font-medium sm:text-sm">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="w-2/5 sm:w-full pt-5 sm:pt-2 flex justify-center">
            <FormProvider>
              {!showNextStep ? (
                <ContactFormStep1 showStep1={showSteps} pageTitle='machinery-loans-for-exporters-india' />
              ) : (
                <ContactFormStep2
                  showPrevStep={showFirstStep}
                  formType="GetInTouchNew"
                />
              )}
            </FormProvider>
          </div>
        </div>
      </section>
      <ExporterChallenge challenge={MachineryLoanData.Challenge} />
      <HowWork howItWork={MachineryLoanData.HowItWorks} />
      <BuyerCredit buyerCredit={MachineryLoanData.buyerCredit} />

      <section className="bg-white sm:py-[70px] py-[100px] grid place-items-center">
        <div className="container px-3 xm:px-2 ">
          <div>
            <h3 className="mb-6 font-bold text-center text-lg text-secondary">
              {MachineryLoanData.FaqHead}
            </h3>
          </div>
          <div className="w-full text-left bg-white">
            <div className="mx-auto max-w-[1100px] sm:w-auto">
              <AccordianFaq faqData={MachineryLoanData.Faq} />
            </div>
          </div>
        </div>
      </section>
      <BannerCTA data={MachineryLoanData.bannerCTA} targetRef={topSectionRef} />
    </main>
  );
};

export default MachineryLoan;
